import { Camera, CameraResultType } from '@capacitor/camera';
import { IonAvatar, IonButton, IonButtons, IonCard, IonCardContent, IonCol, IonContent, IonFab, IonFabButton, IonFooter, IonGrid, IonHeader, IonIcon, IonImg, IonInput, IonItem, IonLabel, IonMenuButton, IonPage, IonRefresher, IonRefresherContent, IonRow, IonSelect, IonSelectOption, IonSkeletonText, IonSpinner, IonTextarea, IonThumbnail, IonTitle, IonToolbar, RefresherEventDetail, useIonAlert, useIonRouter, useIonToast, useIonViewDidEnter, useIonViewWillEnter } from '@ionic/react';
import { addCircleOutline, cameraOutline, chevronBackOutline, pawOutline, refreshOutline, removeCircleOutline, trashOutline } from 'ionicons/icons';
import React, { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import InitialsAvatar from 'react-initials-avatar';
import 'react-initials-avatar/lib/ReactInitialsAvatar.css';
import { useAppContext } from '../AppProvider';

interface RouteParams {
    token: string;
}

const PetEdit: React.FC = () => {
    const [loading, setLoading] = useState<boolean>(true);
    const [uploading, setUploading] = useState<boolean>(false);
    const [Pet, setPet] = useState<any>({});
    const [petPhoto, setPetPhoto] = useState<string>('');
    const [nameEdit, setNameEdit] = useState<boolean>(false);
    const [breedEdit, setBreedEdit] = useState<boolean>(false);
    const [hairEdit, setHairEdit] = useState<boolean>(false);
    const [weightEdit, setWeightEdit] = useState<boolean>(false);
    const [medicalEdit, setMedicalEdit] = useState<boolean>(false);
    const [showAlert] = useIonAlert();

    const { authKey, authenticateMember, setShowLoader,settings, SWFullHorizLogo, SWTextDarkLogo, fadeInImage, showToast, nl2br } = useAppContext();
    
    const { token } = useParams<RouteParams>();
    const router = useIonRouter();
    const weightUnitsInputRef = useRef<any>();

    const petPhotoPath = '//api.besolowise.com/pets/photos/files/';  

    //
    // Authenticate Member
    //
    useIonViewWillEnter(() => { 
        setShowLoader(true);
        const authenticate = async () => {
            const authenticated = await authenticateMember();   
            if(!authenticated || authenticated.mt == '') router.push('/login', 'forward');
            else {
                await getPet(token, authenticated.mt);
                window.setTimeout(() => {
                    setShowLoader(false);
                }, 2000);
            }
        };
        authenticate();
    }); 

    const toggleNameForm = () => setNameEdit(!nameEdit);
    const toggleBreedForm = () => setBreedEdit(!breedEdit);
    const toggleHairForm = () => setHairEdit(!hairEdit);
    const toggleWeightForm = () => setWeightEdit(!weightEdit);
    const toggleMedicalForm = () => setMedicalEdit(!medicalEdit);

    const getPet = async (pt: string, mt: string) => {
        try {
            const formData = new FormData();
            formData.append('mt', mt);
            formData.append('pt', pt);
            const response = await fetch('https://api.besolowise.com/pets/get/', {
                method: 'POST',
                credentials: 'include',
                body: formData
            });
            
            if(response.ok) {
                const obj = await response.json();
                if(typeof obj.petID == 'number') {
                    setPet(obj); 
                    const date = new Date();
                    const time = date.getTime();
                    setPetPhoto(obj.petPhotoUrl);
                    //setPetPhoto(petPhotoPath + `${obj.petPhoto}` + '?v=' + time);
                    setTimeout(() => setLoading(false), 100);
                } else {
                    setLoading(false);
                    showToast({
                        message: 'Data Error',
                        duration: 2000,
                        color: 'danger',
                        position: 'middle'
                    });
                }
            } else {
                setLoading(false);
                showToast({
                    message: 'Response Error',
                    duration: 2000,
                    color: 'danger',
                    position: 'middle'
                });
            }
        } catch(error) {
            setLoading(false);
            showToast({
                message: 'Connection Error: ' + error + '. Refresh this page to try again.',
                duration: 3000,
                color: 'danger',
                position: 'middle'
            });
        }       
    };

    const takePhoto = async (e: any) => {
        const target = e.target;
        try{
            const photo = await Camera.getPhoto({
                quality: 99,
                allowEditing: false,
                resultType: CameraResultType.Base64,
            });
            const img = `data:image/jpeg;base64,${photo.base64String}`;
            updatePetPhoto(img);
        } catch (error) {
            console.log('Photo cancelled');
         }
    };

    const updatePetPhoto = async (imageData: string) => {
        setUploading(true);
        const btn: any = document.getElementById('addPhoto');
        btn.setAttribute('disabled', '');
        const formName = 'photoForm';
        const formData = new FormData();
        const blob = await (await fetch(imageData)).blob();
        formData.append('token', token);
        formData.append('mt', authKey.mt);
        formData.append('formName', formName);
        formData.append('petPhoto', blob, 'temp-image.jpg');
    
        try {
            const response = await fetch('https://api.besolowise.com/pets/update/', {
                method: 'POST',
                body: formData,
                credentials: 'include',
            });
            
            if(response.ok) {
                const obj = await response.json();
                if(obj.success) {
                    getPet(token, authKey.mt);             
                    setUploading(false);
                    showToast({
                        message: 'Your photo was updated',
                        duration: 2000,
                        color: 'success',
                    });
                } else {
                    setUploading(false);
                    showToast({
                        message: 'Error: Image upload failed.',
                        duration: 2000,
                        color: 'danger',
                    });
                }
            } else {
                setUploading(false);
                showToast({
                    message: 'Error: Could not connect to server.',
                    duration: 2000,
                    color: 'danger',
                });
            }
        } catch (error) {
            setUploading(false);
            showToast({
                message: 'Error: ' + error,
                duration: 2000,
                color: 'danger',
            });
        }
        btn.removeAttribute('disabled');
    };

    const rotatePhoto = async (e: any) => {
        const btn: any = document.getElementById('rotatePhoto');
        btn.setAttribute('disabled', '');
        const file =  `${Pet.petPhoto}`;
        const formData = new FormData();
        formData.append('file', file);
        formData.append('mt', authKey.mt);
        try {            
            const response = await fetch('https://api.besolowise.com/pets/photos/rotate/', {
                method: 'POST',
                credentials: 'include',
                body: formData
            });
            
            if(response.ok) {
                const obj = await response.json(); 
                btn.removeAttribute('disabled');
                if(obj.success) getPet(token, authKey.mt);
                else {
                    showToast({
                        message: 'Data Error',
                        duration: 2000,
                        color: 'danger',
                    });
                }
            } else {
                btn.removeAttribute('disabled');
                showToast({
                    message: 'Response Error',
                    duration: 2000,
                    color: 'danger',
                });
            }
        } catch (error) {
            btn.removeAttribute('disabled');
            showToast({
              message: 'Connection Error: ' + error,
              duration: 3000,
              color: 'danger',
            });
        }
    };

    const deletePhoto = async (e: any) => {        
        const field = 'petPhoto';
        const file =  `${Pet.petPhoto}`;
        showAlert({
            header: 'Confirm!',
            message: 'Are you sure you want to DELETE this photo?',
            buttons: [
                { text: 'Cancel', 
                  role: 'cancel', 
                  cssClass: 'alert-button-cancel',
                },
                {
                    text: 'Delete',
                    role: 'confirm', 
                    cssClass: 'alert-button-confirm',
                    handler: async () => {
                        const formData = new FormData();
                        formData.append('field', field);
                        formData.append('file', file);
                        formData.append('token', token);
                        formData.append('mt', authKey.mt);

                        try {
                            const response = await fetch('https://api.besolowise.com/pets/photos/delete/', {
                                method: 'POST',
                                credentials: 'include',
                                body: formData
                            });

                            if(response.ok) {
                                const obj = await response.json(); 
                                if(obj.success) {
                                    getPet(token, authKey.mt);
                                    showToast({
                                        message: 'Photo was deleted.',
                                        duration: 2000,
                                        color: 'success',
                                    });
                                } else {
                                    showToast({
                                        message: 'Data Error: Photo was not deleted. Try again.',
                                        duration: 2000,
                                        color: 'danger',
                                    });
                                }
                            } else {
                                showToast({
                                    message: 'Response Error: Photo was not deleted. Try again.',
                                    duration: 3000,
                                    color: 'danger',
                                    position: 'middle'
                                });
                            }
                        } catch (error) {
                            showToast({
                                message: 'Connection Error: Photo was not deleted. ' + error + ' Try again.',
                                duration: 3000,
                                color: 'danger',
                            });
                        }
                    }
                }
            ]
        })        
    };

    const updatePet = async (e: any) => {
        e.preventDefault();
        const form = e.target; 
        const formName = form.name;
        const btn: any = document.getElementById(formName + 'Submit');
        btn.innerHTML = 'Saving...';
        btn.setAttribute('disabled', '');
        const formData = new FormData(form);
        formData.append('formName', formName);
        formData.append('token', token);
        formData.append('mt', authKey.mt);

        try {
            const response = await fetch('https://api.besolowise.com/pets/update/', {
                method: 'POST',
                credentials: 'include',
                body: formData
            });
            if(response.ok) {
                const obj = await response.json(); 
                if(obj.success) {
                    getPet(obj.pt, authKey.mt);
                    btn.innerHTML = 'Save';
                    btn.removeAttribute('disabled');
                    document.getElementById(formName + 'Cancel')?.click();
                    showToast({
                        message: 'SAVED',
                        duration: 2000,
                        color: 'success',
                    });
                } else {
                    btn.innerHTML = 'Save';
                    btn.removeAttribute('disabled');
                    showToast({
                        message: 'Error: ' + obj.message,
                        duration: 3000,
                        color: 'danger',
                    });
                }
            } else {
                btn.innerHTML = 'Save';
                btn.removeAttribute('disabled');
                showToast({
                    message: 'Response Error',
                    duration: 3000,
                    color: 'danger',
                });
            }              
        } catch(error) {
            btn.innerHTML = 'Save';
            btn.removeAttribute('disabled');
            showToast({
                message: 'Connection Error: ' + error,
                duration: 3000,
                color: 'danger',
            });
        } 
    };

    const deletePet = () => {
        showAlert({
            header: 'Confirm!',
            message: 'Are you sure you want to REMOVE this Pet?',
            buttons: [
                { text: 'Cancel',
                  role: 'cancel', 
                  cssClass: 'alert-button-cancel', 
                },
                {
                    text: 'Remove',
                    role: 'confirm',
                    cssClass: 'alert-button-confirm',
                    handler: async () => {
                        try {
                            const formData = new FormData();
                            formData.append('token', token);
                            formData.append('mt', authKey.mt);
                            const response = await fetch('https://api.besolowise.com/pets/delete/', {
                                method: 'POST',
                                credentials: 'include',
                                body: formData
                            });
                            if(response.ok) {
                                const obj = await response.json(); 
                                if(obj.success) {
                                    router.push('/account/pets', 'back');
                                }  else {
                                    showToast({
                                        message: 'Data error.',
                                        duration: 2000,
                                        color: 'danger',
                                    });
                                }
                            } else {
                                showToast({
                                    message: 'Pet was not deleted',
                                    duration: 2000,
                                    color: 'danger',
                                });
                            }
                        } catch(error) {
                            showToast({
                                message: 'Error deleting pet data: ' + error,
                                duration: 2000,
                                color: 'danger',
                            });
                        }  
                    }
                }
            ]
        })
    };

    const handleRefresh = (event: CustomEvent<RefresherEventDetail>) => {
        setLoading(true);
        setTimeout(async () => {
            await getPet(token, authKey.mt);
            event.detail.complete();
            setLoading(false);
        }, 2000);
    };

    return (
        <IonPage>
            <IonHeader>
                <IonToolbar>
                    <IonButtons slot='start'>
                        <IonButton color={'tertiary'} routerDirection='back' routerLink='/account/pets'>Pets</IonButton>
                    </IonButtons>
                    <IonTitle><IonImg className='headerTextLogo' src={settings.theme == '1' ? SWTextDarkLogo : SWFullHorizLogo}></IonImg></IonTitle>
                    <IonButtons slot='end'>
                        <IonMenuButton color={'tertiary'} />
                    </IonButtons>
                </IonToolbar>
            </IonHeader>
            <IonContent className="ion-padding account-content-bg" id="PetContent">

                <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
                    <IonRefresherContent></IonRefresherContent>
                </IonRefresher>

                <IonGrid>
                    <IonRow>
                        <IonCol size='12'>
                            <h1><IonIcon className='m-hide' color={'secondary'} icon={pawOutline}></IonIcon> 
                                {Pet.petName}
                                <br /><span>Editing a Saved Pet</span>
                            </h1>                            
                        </IonCol>
                    </IonRow>
                </IonGrid>

                <IonCard>                    
                    <IonCardContent>   
                        <IonLabel color={'dark'} onClick={toggleNameForm}><b>Name</b> <em className='required'>Required</em></IonLabel>                     
                        { nameEdit 
                            ? (<form name='nameForm' onSubmit={updatePet}>
                                    <IonInput fill='outline' name='petName' placeholder='Name' type='text' value={Pet.petName}></IonInput>
                                    <IonButton className='ion-margin-top' color={'medium'} id='nameFormCancel' onClick={toggleNameForm}>Cancel</IonButton>
                                    <IonButton className='ion-margin-top' color={'secondary'} id='nameFormSubmit' type='submit'>Save</IonButton>
                                </form>)                                                     
                            : <p onClick={toggleNameForm}>{Pet.petName}</p>
                        }
                    </IonCardContent>
                </IonCard>
                
                <IonCard>                    
                    <IonCardContent>  
                        <IonLabel color={'dark'} onClick={toggleBreedForm}><b>Species & Breed</b> <em className='required'>Required</em></IonLabel>                      
                        { breedEdit 
                            ? (<form name='breedForm' onSubmit={updatePet}>
                                    <IonInput fill='outline' name='petBreed' placeholder='Species & Breed' type='text' value={Pet.petBreed}></IonInput>
                                    <IonButton className='ion-margin-top' color={'medium'} id='breedFormCancel' onClick={toggleBreedForm}>Cancel</IonButton>
                                    <IonButton className='ion-margin-top' color={'secondary'} id='breedFormSubmit' type='submit'>Save</IonButton>
                                </form>)                                                     
                            : <p onClick={toggleBreedForm}>{Pet.petBreed == '' ? 'Not Recorded' : Pet.petBreed}</p>
                        }
                    </IonCardContent>
                </IonCard>
                
                <IonCard>                    
                    <IonCardContent>
                        <IonLabel color={'dark'} onClick={toggleHairForm}><b>Hair Length & Color</b></IonLabel>                     
                        { hairEdit 
                            ? (<form name='hairForm' onSubmit={updatePet}>
                                    <IonGrid>
                                        <IonRow>
                                            <IonCol sizeXs='12' sizeSm='4'>
                                                <IonSelect fill='outline' labelPlacement='stacked' name='petHair' placeholder='Length' value={Pet.petHair}>  
                                                    <IonSelectOption value='Short'>Short</IonSelectOption>
                                                    <IonSelectOption value='Medium'>Medium</IonSelectOption>
                                                    <IonSelectOption value='Long'>Long</IonSelectOption>
                                                </IonSelect>
                                            </IonCol> 
                                            <IonCol sizeXs='12' sizeSm='8'>
                                                <IonInput fill='outline' name='petColor' placeholder='Color' type='text' value={Pet.petColor}></IonInput>
                                            </IonCol>                      
                                        </IonRow>
                                    </IonGrid>
                                    <IonButton className='ion-margin-top' color={'medium'} id='hairFormCancel' onClick={toggleHairForm}>Cancel</IonButton>
                                    <IonButton className='ion-margin-top' color={'secondary'} id='hairFormSubmit' type='submit'>Save</IonButton>
                                </form>)                                                     
                            : <p onClick={toggleHairForm}>{(Pet.petHair == '' && Pet.petHair == '') ? 'Not Recorded' : Pet.petHair + ' ' + Pet.petColor}</p>
                        }
                    </IonCardContent>
                </IonCard>
                
                <IonCard>
                    <IonCardContent>
                        <IonLabel color={'dark'} onClick={toggleWeightForm}><b>Approx. Weight</b></IonLabel>
                        { weightEdit 
                            ? (<form name='weightForm' onSubmit={updatePet}>
                                    <IonGrid>
                                        <IonRow>
                                            <IonCol size='6'>
                                                <IonInput fill='outline' name='petWeight' placeholder='Weight' type='number' value={Pet.petWeight}></IonInput>
                                            </IonCol>
                                            <IonCol size='6'>
                                                <IonSelect fill='outline' labelPlacement='stacked' name='petWeightUnits' placeholder='Units' ref={weightUnitsInputRef} value={Pet.petWeightUnits}>  
                                                    <IonSelectOption value='LB'>LB</IonSelectOption>
                                                    <IonSelectOption value='KG'>KG</IonSelectOption>
                                                </IonSelect>
                                            </IonCol>                                   
                                        </IonRow>
                                    </IonGrid>
                                    <IonButton className='ion-margin-top' color={'medium'} id='weightFormCancel' onClick={toggleWeightForm}>Cancel</IonButton>
                                    <IonButton className='ion-margin-top' color={'secondary'} id='weightFormSubmit' type='submit'>Save</IonButton>
                                </form>)                                                     
                            : <p onClick={toggleWeightForm}>{Pet.petWeight == '0' ? 'Not Recorded' : Pet.petWeight + ' ' + Pet.petWeightUnits}</p>
                        }
                    </IonCardContent>
                </IonCard>

                <IonCard>                    
                    <IonCardContent>  
                        <IonLabel color={'dark'} onClick={toggleMedicalForm}><b>Medical, Feeding, or Other Important Info</b></IonLabel>                      
                        { medicalEdit 
                            ? (<form name='medicalForm' onSubmit={updatePet}>
                                    <IonTextarea fill='outline' name='petMedical' placeholder='Medical or Other Important Info' value={Pet.petMedical}></IonTextarea>
                                    <IonButton className='ion-margin-top' color={'medium'} id='medicalFormCancel' onClick={toggleMedicalForm}>Cancel</IonButton>
                                    <IonButton className='ion-margin-top' color={'secondary'} id='medicalFormSubmit' type='submit'>Save</IonButton>
                                </form>)                                                     
                            : <p onClick={toggleMedicalForm}>{Pet.petMedical == '' ? 'Not Recorded' : (<span dangerouslySetInnerHTML={{ __html: nl2br(Pet.petMedical) }} />)}</p>
                        }
                    </IonCardContent>
                </IonCard>

                <IonCard className='ion-text-center'>
                    <IonCardContent>
                        <IonLabel color={'dark'}><b>Photo</b></IonLabel>   
                        <p className='ion-margin-top'>Photos must be .jpg format.</p>                     
                        <IonThumbnail>
                            {uploading
                                ? (<div className='no-user-photo'><IonSpinner name="crescent"></IonSpinner></div>)
                                : (Pet.petPhoto == ''
                                    ? (<div className='no-user-photo' onClick={takePhoto}><IonIcon icon={cameraOutline}></IonIcon></div>)  
                                    : (<IonImg src={petPhoto} onIonImgDidLoad={fadeInImage} />)
                                )
                            } 
                        </IonThumbnail>                                
                        {Pet.petPhoto == ''
                            ? (<IonButton className='ion-margin' color={'secondary'} id='addPhoto' onClick={takePhoto}><IonIcon slot="start" icon={addCircleOutline}></IonIcon> Add Photo</IonButton>)
                            : (<>
                                <IonButton className='ion-margin' color={'tertiary'} fill='outline' id='rotatePhoto' onClick={rotatePhoto} shape="round" size='default'><IonIcon icon={refreshOutline} slot='start'></IonIcon> Rotate</IonButton>
                                <IonButton className='ion-margin' color={'remove'} fill='outline' id='deletePhoto' onClick={deletePhoto} shape="round" size='default'><IonIcon icon={trashOutline} slot='start'></IonIcon> Delete</IonButton>
                                </>) 
                        } 
                    </IonCardContent>
                </IonCard>

                <div className='ion-text-center'>
                    <IonButton className='ion-margin delete-button-outline' color={'remove'} fill="outline" onClick={deletePet}><IonIcon icon={trashOutline} slot='start'></IonIcon> Remove Pet</IonButton>
                </div>

            </IonContent>
        </IonPage>
    );
};

export default PetEdit;
